import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Stack,
  Typography,
} from "@mui/joy";
import to from "await-to-js";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { getAfterAuthPageRedirect } from "../utils/helpers";
import { authApiService } from "~/common/api/services/auth-api-service";
import {
  signInSchema,
  type SignInFormData,
} from "~/common/api/validation/auth-schemas";
import { RoutePaths } from "~/common/constants/route-paths";

/**
 * A React functional component for the "Sign In" page.
 * Provides UI elements for users to log into their account.
 */
const SignIn = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
  });

  const onFormSubmit = async (data: SignInFormData) => {
    const [error] = await to(authApiService.signIn(data));

    if (error) {
      // Handle API errors
      setError("root", {
        type: "manual",
        message: error.message,
      });
    } else {
      // Navigate to applications page
      void navigate(getAfterAuthPageRedirect());
    }
  };

  return (
    <>
      <Stack sx={{ gap: 4, mb: 2 }}>
        <Stack sx={{ gap: 1 }}>
          <Typography component="h1" level="h3">
            Увійти
          </Typography>
          <Typography level="body-sm">
            Перший раз тут?{" "}
            <Link
              level="title-sm"
              onClick={() => void navigate(RoutePaths.PRICING)}
            >
              Зареєструватись!
            </Link>
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ gap: 4 }}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <FormControl required error={!!errors.email}>
            <FormLabel>Електронна пошта</FormLabel>
            <Input
              type="email"
              placeholder="ivan@example.com"
              {...register("email")}
            />
            {errors.email && (
              <FormHelperText>{errors.email.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl required error={!!errors.password}>
            <FormLabel>Пароль</FormLabel>
            <Input
              type="password"
              placeholder="123456"
              {...register("password")}
            />
            {errors.password && (
              <FormHelperText>{errors.password.message}</FormHelperText>
            )}
          </FormControl>
          {errors.root && (
            <FormHelperText sx={{ color: "danger.500" }}>
              {errors.root.message}
            </FormHelperText>
          )}
          <Stack sx={{ gap: 4, mt: 2 }}>
            <Button type="submit" fullWidth loading={isSubmitting}>
              Увійти
            </Button>
          </Stack>
        </form>
      </Stack>
    </>
  );
};

export default SignIn;
